import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  shortNoteTitle,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  beforeSubtitle,
  fullUrl,
  longDate,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import { doiUrl } from "@/lib/tool-kit/special-sources/common";

/*
 * A preprint on bioRxiv, medRxiv, SSRN, PsyArXiv, OSF Preprints or Research Square, in
 * APA 7, MLA 9 and Chicago 18. (arXiv has its own generator.)
 *
 * Sources checked (October 2026):
 * - APA 7: the APA Style page "Preprint article references" gives author, year, italic
 *   title, then the archive name — not italic — and a DOI link, for example Hampton et
 *   al. (2017) on PsyArXiv; "other preprint archives follow the same format". Library
 *   guides that quote it (Notre Dame Australia: "Tillman, G. (2020). Disordered social
 *   media use … PsyArXiv. https://doi.org/10.31234/osf.io/dbg62") print the same shape.
 *   APA's site blocks automated reading, so the page itself was not opened here.
 * - MLA 9: the Handbook has no preprint example. The MLA Style Center's answer on
 *   working papers — "also called prepublication or preprint versions" — puts the
 *   repository in the container slot in italics ("CORE, 2018, dx.doi.org/…") and allows
 *   the label "Working paper." in the optional element at the end; "Preprint." here is
 *   that same device. MLA 9 prints a DOI as a https://doi.org/ link.
 * - Chicago 18: §14.76 treats a preprint as unpublished material, labelled "Preprint"
 *   with the server and the posting date. The CMOS 18 sample as reproduced by Monash
 *   University: Fiallo, Kathy, and Alexia Galati. 2022. "The Influence of Auditory
 *   Processing Difficulties on Perceptual Learning." Preprint, PsyArXiv, September 4.
 *   https://doi.org/10.31234/osf.io/p723u. This generator uses that shape for the
 *   notes-and-bibliography system; wrapping the details in parentheses in a note is
 *   the usual Chicago pattern for notes, not a quoted sample.
 * - DOI prefixes: bioRxiv and medRxiv 10.1101, PsyArXiv 10.31234, OSF Preprints
 *   10.31219, SSRN 10.2139, Research Square 10.21203 (from each server's own record
 *   pages).
 */

export const PREPRINT_FIELDS = {
  authors: "authors",
  title: "title",
  server: "server",
  serverOther: "serverOther",
  posted: "posted",
  link: "link",
} as const;

const F = PREPRINT_FIELDS;

export type PreprintServer =
  | "biorxiv"
  | "medrxiv"
  | "ssrn"
  | "psyarxiv"
  | "osf"
  | "researchsquare";

export const PREPRINT_SERVERS: readonly PreprintServer[] = [
  "biorxiv",
  "medrxiv",
  "ssrn",
  "psyarxiv",
  "osf",
  "researchsquare",
];

/** How each server writes its own name. */
export const PREPRINT_SERVER_NAMES: Readonly<Record<PreprintServer, string>> = {
  biorxiv: "bioRxiv",
  medrxiv: "medRxiv",
  ssrn: "SSRN",
  psyarxiv: "PsyArXiv",
  osf: "OSF Preprints",
  researchsquare: "Research Square",
};

export interface PreprintLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Preprint {
  authors: PersonName[];
  title: string;
  server: string;
  posted: PartialDate | null;
  link: string;
  /** True when the link is a DOI, which MLA keeps as a https://doi.org/ address. */
  isDoi: boolean;
}

/** The server named in the form: a typed name wins, then the list, then bioRxiv. */
export function preprintServerName(values: MediaValues): string {
  const typed = get(values, F.serverOther);
  if (typed) {
    return typed;
  }
  const chosen = get(values, F.server) as PreprintServer;
  return PREPRINT_SERVER_NAMES[chosen] ?? PREPRINT_SERVER_NAMES.biorxiv;
}

function readPreprint(values: MediaValues): Preprint {
  const raw = get(values, F.link);
  const doi = doiUrl(raw);
  return {
    authors: parsePeople(get(values, F.authors)),
    title: capitalizeSubtitle(get(values, F.title)),
    server: preprintServerName(values),
    posted: parsePartialDate(get(values, F.posted)),
    link: doi ?? fullUrl(raw),
    isDoi: doi !== null,
  };
}

/* ---------------------------------------------------------------------- APA */

function apaYear(preprint: Preprint): string {
  return preprint.posted ? String(preprint.posted.year) : "n.d.";
}

function apaReference(preprint: Preprint): CitationParts {
  const year = apaYear(preprint);
  const source = [`${preprint.server}${closer(preprint.server)}`, preprint.link]
    .filter(Boolean)
    .join(" ");
  const stop = closer(preprint.title);
  if (preprint.authors.length === 0) {
    return tidyParts([
      italic(preprint.title),
      plain(`${stop} (${year}). ${source}`),
    ]);
  }
  const authors = apaAuthors(preprint.authors);
  return tidyParts([
    plain(`${authors}${closer(authors)} (${year}). `),
    italic(preprint.title),
    plain(`${stop} ${source}`),
  ]);
}

function apaOutput(preprint: Preprint, labels: PreprintLabels): CitationOutput {
  const year = apaYear(preprint);
  const has = preprint.authors.length > 0;
  const short = beforeSubtitle(titleCase(preprint.title));
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(preprint) },
      {
        label: labels.parenthetical,
        parts: has
          ? [plain(`(${apaInTextAuthors(preprint.authors)}, ${year})`)]
          : [plain("("), italic(short), plain(`, ${year})`)],
      },
      {
        label: labels.narrative,
        parts: has
          ? [plain(`${apaInTextAuthors(preprint.authors, true)} (${year})`)]
          : [italic(short), plain(` (${year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaWorksCited(preprint: Preprint): CitationParts {
  const title = titleCase(preprint.title);
  const link = preprint.isDoi ? preprint.link : mlaUrl(preprint.link);
  const location = [mlaDate(preprint.posted), link].filter(Boolean).join(", ");
  const parts: CitationParts = [];
  if (preprint.authors.length > 0) {
    const authors = mlaAuthors(preprint.authors);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  parts.push(plain(`“${title}${closer(title)}” `));
  parts.push(italic(preprint.server));
  parts.push(plain(`${location ? `, ${location}` : ""}. Preprint.`));
  return tidyParts(parts);
}

function mlaOutput(preprint: Preprint, labels: PreprintLabels): CitationOutput {
  const inText =
    preprint.authors.length > 0
      ? mlaInTextAuthors(preprint.authors)
      : `“${beforeSubtitle(titleCase(preprint.title))}”`;
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(preprint) },
      { label: labels.inText, parts: [plain(`(${inText})`)] },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoNote(preprint: Preprint, title: string): CitationParts {
  const details = ["preprint", preprint.server, longDate(preprint.posted)]
    .filter(Boolean)
    .join(", ");
  const lead =
    preprint.authors.length > 0
      ? `${chicagoNoteAuthors(preprint.authors)}, `
      : "";
  const tail = preprint.link ? `, ${preprint.link}.` : ".";
  return [plain(`${lead}“${title}” (${details})${tail}`)];
}

function chicagoShortNote(preprint: Preprint, title: string): CitationParts {
  const short = shortNoteTitle(title);
  const quoted = `“${short}${closer(short)}”`;
  if (preprint.authors.length === 0) {
    return [plain(quoted)];
  }
  return [plain(`${chicagoShortAuthors(preprint.authors)}, ${quoted}`)];
}

function chicagoBibliography(preprint: Preprint, title: string): CitationParts {
  const sentences: string[] = [];
  if (preprint.authors.length > 0) {
    const authors = chicagoBibliographyAuthors(preprint.authors);
    sentences.push(`${authors}${closer(authors)}`);
  }
  sentences.push(`“${title}${closer(title)}”`);
  const details = ["Preprint", preprint.server, longDate(preprint.posted)]
    .filter(Boolean)
    .join(", ");
  sentences.push(`${details}.`);
  if (preprint.link) {
    sentences.push(`${preprint.link}.`);
  }
  return [plain(sentences.join(" "))];
}

function chicagoOutput(
  preprint: Preprint,
  labels: PreprintLabels
): CitationOutput {
  const title = titleCase(preprint.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(preprint, title) },
      { label: labels.shortNote, parts: chicagoShortNote(preprint, title) },
      {
        label: labels.bibliography,
        parts: chicagoBibliography(preprint, title),
      },
    ],
    note: labels.chicagoNote,
  };
}

export function preprintIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatPreprint(
  values: MediaValues,
  labels: PreprintLabels
): CitationOutput[] {
  const preprint = readPreprint(values);
  return [
    apaOutput(preprint, labels),
    mlaOutput(preprint, labels),
    chicagoOutput(preprint, labels),
  ];
}
